import { useAiDraftQuery } from 'lib-knowledge-management-common/graphql';
import { useMemo } from 'react';

import { Suggestion, toSuggestion } from '../utils/toSuggestion';

import { useCategoryNames } from './useCategoryNames';
import { useCollectionNames } from './useCollectionNames';
import { useSeedCategoryTitles } from './useSeedCategoryTitles';

export type UseSuggestion = {
  suggestion: Suggestion | undefined;
  /** Fetching a draft the cache did not already hold. */
  loading: boolean;
  /** The draft could not be read. Distinct from a draft that is simply gone. */
  failed: boolean;
  /** Ask for the draft again, for the reviewer to call after a failure. */
  retry: () => void;
};

/**
 * The draft with this id, as it stands now.
 *
 * Queried rather than handed down from the list, for two reasons. A copy taken
 * when the row was clicked would keep showing the body the AI has just
 * replaced, and would still think the draft unfiled after it had been moved —
 * `useQuery` watches its result in the cache, and `aiDraftUpdate` returns these
 * same fields into the same entity, so a revision or a re-file refreshes the
 * preview without a refetch. And reading it by id means a draft the list has
 * not loaded still resolves, which a link straight to one depends on.
 *
 * `cache-first` so a draft the list already wrote costs nothing to show. That
 * relies on the `Query.aiDraft` read policy in `apolloClient.ts` pointing the
 * field at the `AiDraft` entity — without it the cache holds the draft but not
 * the field, and every selection would go to the network.
 *
 * The suggestion is undefined once the draft is gone — approved or rejected —
 * which the caller reads as "nothing to show". A draft that could not be *read*
 * is reported separately, since that wants a message rather than a blank pane.
 */
export function useSuggestion(id: string): UseSuggestion {
  const categoryName = useCategoryNames();

  const { data, loading, error, refetch } = useAiDraftQuery({
    variables: { id },
    fetchPolicy: 'cache-first',
  });

  const draft = data?.aiDraft;

  // Empty until the draft is there, since a hook cannot run conditionally.
  const collectionIds = useMemo(
    () => (draft?.collectionId ? [draft.collectionId] : []),
    [draft?.collectionId],
  );

  const collectionNames = useCollectionNames(collectionIds);
  useSeedCategoryTitles(collectionIds);

  return {
    suggestion: draft
      ? toSuggestion(draft, collectionNames, categoryName)
      : undefined,
    loading,
    failed: !!error && !draft,
    retry: () => {
      // The rejection is already reported through `error`; this only stops an
      // unhandled rejection when the retry fails too.
      refetch().catch(() => undefined);
    },
  };
}
